import { useCallback, useEffect, useRef, useState } from "react";
import AdminService from "../AdminService";

export function useCustomers() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [globalFilter, setGlobalFilter] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [sorting, setSortingState] = useState([]);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
    const requestIdRef = useRef(0);
    const isFirstRenderRef = useRef(true);

    // ── Debounce globalFilter → debouncedSearch (900ms) ──────────────────
    useEffect(() => {
        // Skip on initial mount — avoids creating a new pagination object reference
        // that would re-trigger fetchCustomers and cause a duplicate API call.
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedSearch(globalFilter);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }, 900);
        return () => clearTimeout(timer);
    }, [globalFilter]);

    // ── Server-side data fetch ────────────────────────────────────────────
    const fetchCustomers = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);

        try {
            const sort = sorting?.[0];
            const res = await AdminService.getManageCustomers({
                page: pagination.pageIndex + 1,
                pageSize: pagination.pageSize,
                search: debouncedSearch || undefined,
                sortBy: sort?.id,
                sortOrder: sort ? (sort.desc ? "desc" : "asc") : undefined,
            });
            if (requestId !== requestIdRef.current) return;
            setData(res?.data ?? []);
            setTotalCount(res?.pagination?.total ?? 0);
        } catch (err) {
            if (requestId !== requestIdRef.current) return;
            console.error("[Customers] API error:", err);
            setError(err);
            setData([]);
            setTotalCount(0);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [pagination, sorting, debouncedSearch]);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    // ── Sorting handler — resets to page 1 on every sort change ──────────
    const setSorting = useCallback((updater) => {
        setSortingState(updater);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, []);

    return {
        data,
        loading,
        error,
        totalCount,
        sorting,
        setSorting,
        globalFilter,
        setGlobalFilter,
        pagination,
        setPagination,
        refetch: fetchCustomers,
    };
}